/**
 * テーマ横断の単語一覧。
 *
 * ステージに属さない語(未使用語)も含めて、単語セット全体を検索・タグ絞り込みできる。
 * 「この訳あってる?」を探しに来る用途なので、検索が空のときはタグを選ばせる。
 */
import { useMemo, useState } from 'react';
import { searchWords, tagCounts, type LanguageContent } from '../services/content';
import type { RequestTarget } from './RequestDialog';
import { WordTable } from './WordTable';

interface Props {
  readonly content: LanguageContent;
  readonly assetsBase: string | null;
  readonly onRequest: (target: RequestTarget) => void;
}

/** タグ選択の候補として出す上限(全部出すと数百個になる)。 */
const TAG_LIMIT = 60;
/** 一覧の表示上限。 */
const RESULT_LIMIT = 300;

export function WordBrowser({ content, assetsBase, onRequest }: Props) {
  const [query, setQuery] = useState('');
  const [tag, setTag] = useState('');

  const allWords = useMemo(() => [...content.wordsById.values()], [content.wordsById]);
  const tags = useMemo(() => tagCounts(allWords).slice(0, TAG_LIMIT), [allWords]);

  const results = useMemo(() => {
    const byTag = tag === '' ? allWords : allWords.filter((word) => word.tags?.includes(tag));
    if (query.trim() === '') return byTag.slice(0, RESULT_LIMIT);
    return searchWords(byTag, query, RESULT_LIMIT);
  }, [allWords, query, tag]);

  return (
    <div className="workspace">
      <div className="pane pane--full">
        <div className="toolbar">
          <input
            className="input"
            style={{ maxWidth: '320px' }}
            type="search"
            placeholder="語・読み・訳・タグ・ID で検索"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
          <select
            className="select"
            style={{ maxWidth: '260px' }}
            value={tag}
            onChange={(event) => {
              setTag(event.target.value);
            }}
          >
            <option value="">タグで絞り込む(すべて)</option>
            {tags.map(([name, count]) => (
              <option key={name} value={name}>
                {name}({String(count)})
              </option>
            ))}
          </select>
          <span className="muted mono">
            {String(results.length)} 件表示 / 全 {String(allWords.length)} 語
          </span>
        </div>

        <WordTable
          words={results}
          assetsBase={assetsBase}
          stageIdsByWordId={content.stageIdsByWordId}
          onRequest={(word) => {
            onRequest({
              type: 'word',
              id: word.id,
              label: word.display,
              language: content.language,
              defaultKind: 'meaning',
            });
          }}
        />
      </div>
    </div>
  );
}
